import { LoadingState } from "@repo/app-types/client/client";
import type { AdminApiEntitlementUsage } from "@repo/app-types/entities/entitlement";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import {
  useAiUsageQuery,
  useCompanyEntitlementsQuery,
  useLicenseUtilizationQuery,
} from "../../../api/entities/company.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  fetchBillingInfo,
  selectDefaultPaymentMethod,
} from "../../billing/billingSlice.js";
import { selectCompany } from "../../company/companySlice.js";
import { PlanOverviewCard } from "./planOverviewCard.js";

export const PlanSection: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const [portalLoadingState, setPortalLoadingState] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const defaultPaymentMethod = useAppSelector(selectDefaultPaymentMethod);
  const {
    data: entitlements,
    isLoading: isLoadingEntitlements,
    isError: isErrorEntitlements,
  } = useCompanyEntitlementsQuery();
  const { data: licenseUtilization } = useLicenseUtilizationQuery();
  // The current period's usage; deduped with AiCreditsSection's initial query.
  const { data: currentUsage } = useAiUsageQuery({ periodOffset: 0 });

  useEffect(() => {
    dispatch(fetchBillingInfo());
  }, [dispatch]);

  const openStripeBillingPortal = async (isPaymentMethodUpdate?: boolean) => {
    setPortalLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const path = `/billing/portal-session${
        isPaymentMethodUpdate ? "?type=payment_method_update" : ""
      }`;
      const { data } = await axios.post(path);
      setPortalLoadingState(LoadingState.LOADED);

      // Navigate to the portal url rather than opening in popup
      // so we don't have listen to webhooks when payment methods are added
      window.location.href = data;
    } catch {
      setPortalLoadingState(LoadingState.ERROR);
    }
  };

  const entitlementColumns: readonly EntityTableColumnDefinition<AdminApiEntitlementUsage>[] =
    useMemo(
      () => [
        {
          id: "starts_at",
          isRowHeader: true,
          header: "Start date",
          className: "w-[25%]",
          renderCell: (row) =>
            DateTime.fromISO(row.starts_at).toFormat("MMM dd yyyy"),
        },
        {
          id: "expires_at",
          header: "Expiration date",
          className: "w-[25%]",
          renderCell: (row) =>
            DateTime.fromISO(row.expires_at).toFormat("MMM dd yyyy"),
        },
        {
          id: "max_interviews",
          header: "Max interviews",
          className: "w-[25%]",
          renderCell: (row) => row.max_interviews.toString(),
        },
        {
          id: "used_interviews",
          header: "Used interviews",
          className: "w-[25%]",
          renderCell: (row) => row.used_interviews.toString(),
        },
      ],
      [],
    );

  if (!company) {
    return null;
  }

  return (
    <div className="space-y-6">
      <PlanOverviewCard
        plan={company.billing_current_plan}
        paymentMethod={defaultPaymentMethod}
        licenseUtilization={licenseUtilization}
        aiUsage={currentUsage}
        portalLoadingState={portalLoadingState}
        onManagePlan={
          company.billing_company_id
            ? () => openStripeBillingPortal()
            : undefined
        }
        onUpdatePaymentMethod={() => {
          const isPaymentMethodUpdate = true;
          openStripeBillingPortal(isPaymentMethodUpdate);
        }}
      />
      {entitlements && entitlements.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">
            Plan limits
          </h2>
          <EntityTable
            aria-label="Entitlements"
            columns={entitlementColumns}
            entities={entitlements}
            isLoading={isLoadingEntitlements}
            isError={isErrorEntitlements}
            emptyMessage="No entitlements found."
          />
        </section>
      ) : null}
    </div>
  );
};
